import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Clock, RefreshCw, Monitor, Lightbulb, Sparkles,
  ThumbsUp, ThumbsDown, CheckCircle,
  ArrowRight, Users, MapPin, Zap, CheckCircle2, LaptopMinimal,
} from 'lucide-react';
import { BlockItem } from '../types/helpArticles';
import MediaPlaceholder from '../components/MediaPlaceholder';
import HelpVideo from '../components/HelpVideo';
import ZoomableImage from '../components/ZoomableImage';
import { topicByTitle } from '../data/helpTopics';
import { useArticle } from '../hooks/useArticle';
import { parseMarkdown } from '../utils/parseMarkdown';
import { articleDescription, articlePath, articleTitle, articleUrl, topicPath } from '../seo/helpMeta';
import '../App.css';

/* ---- Chip icon map ---- */
const CHIP_ICONS: Record<string, React.ReactNode> = {
  'users': <Users size={13} />,
  'map-pin': <MapPin size={13} />,
  'zap': <Zap size={13} />,
  'monitor': <Monitor size={13} />,
  'laptop-minimal': <LaptopMinimal size={13} />,
  'check': <CheckCircle2 size={13} />,
};
const ChipIcon: React.FC<{ name: string }> = ({ name }) => <>{CHIP_ICONS[name] || null}</>;

/* ---- Block renderer ---- */
function renderBlock(block: BlockItem, idx: number, ctx?: { topic?: string; title?: string }): React.ReactNode {
  switch (block.k) {
    case 'h2':
      return <h2 id={block.id} key={idx}>{block.t}</h2>;
    case 'h3':
      return <h3 key={idx}>{block.t}</h3>;
    case 'p':
      return <p key={idx}>{parseMarkdown(block.c)}</p>;
    case 'steps':
      return (
        <ol className="tut-substeps" key={idx}>
          {block.items.map((item, i) => <li key={i}>{parseMarkdown(item)}</li>)}
        </ol>
      );
    case 'bullets':
      return (
        <ul className="art-bullets" key={idx}>
          {block.items.map((item, i) => <li key={i}>{parseMarkdown(item)}</li>)}
        </ul>
      );
    case 'chips':
      return (
        <div className="tut-chips" key={idx}>
          {block.items.map((chip, i) => (
            <span key={i} className={`tut-chip${chip.tone ? ` tut-chip--${chip.tone}` : ''}`}>
              {chip.icon && <ChipIcon name={chip.icon} />}
              {chip.t}
            </span>
          ))}
        </div>
      );
    case 'keys':
      return (
        <div className="art-keys" key={idx}>
          {block.items.map((row, i) => (
            <div className="art-keys-row" key={i}>
              <kbd className="art-key">{row.g}</kbd>
              <span className="art-keys-action">{row.a}</span>
            </div>
          ))}
        </div>
      );
    case 'callout':
      return (
        <div className={`tut-callout tut-callout--${block.kind}`} key={idx}>
          <span className="ca-icon">
            {block.kind === 'tip' ? <Lightbulb size={18} /> : <Sparkles size={18} />}
          </span>
          <div className="ca-body">
            <strong>{block.title}</strong>
            {parseMarkdown(block.c)}
          </div>
        </div>
      );
    case 'media': {
      return (
        <figure className="tut-step-media" key={idx}>
          {block.kind === 'video' ? (
            block.src ? (
              <HelpVideo src={block.src} eyebrow={ctx?.topic} title={block.label || ctx?.title} duration={block.duration} />
            ) : (
              <MediaPlaceholder
                eyebrow={ctx?.topic}
                title={block.label || ctx?.title}
                duration={block.duration}
              />
            )
          ) : block.src ? (
            <ZoomableImage src={block.src} alt={block.label || block.tag || 'Screenshot'} />
          ) : (
            <MediaPlaceholder
              kind="shot"
              eyebrow={ctx?.topic}
              // The label is a real description of the shot — "The cloud Bookshelf
              // with your books" — so it beats the article title. Some blocks just
              // say "Screenshot", which tells a reader nothing.
              title={block.label && block.label !== 'Screenshot' ? block.label : ctx?.title}
            />
          )}
          {block.caption && <figcaption className="tut-media-cap">{parseMarkdown(block.caption)}</figcaption>}
        </figure>
      );
    }
    default:
      return null;
  }
}

/* ---- Page component ---- */
const HelpArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { article, loading, error } = useArticle(slug ?? '');

  const [active, setActive] = useState('');
  const [fb, setFb] = useState<'yes' | 'no' | null>(null);

  // Auto-generate TOC from h2 blocks (empty while loading)
  const toc = article
    ? article.blocks
        .filter((b): b is { k: 'h2'; id: string; t: string } => b.k === 'h2')
        .map(b => ({ id: b.id, t: b.t }))
    : [];

  const tocIds = useRef<string[]>([]);
  useEffect(() => {
    tocIds.current = toc.map(s => s.id);
  });

  useEffect(() => {
    if (toc.length > 0) setActive(toc[0].id);
    // toc is excluded: inline-derived array has new ref every render; toc.length is the right signal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, toc.length]);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const vis = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive((vis[0].target as HTMLElement).id);
    }, { rootMargin: '-120px 0px -65% 0px', threshold: 0 });

    const currentIds = tocIds.current;
    currentIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [slug, toc.length]);

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="article-page">
        <div style={{ maxWidth: 600, margin: '80px auto', textAlign: 'center', padding: '0 24px' }}>
          <p style={{ color: '#888' }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-page">
        <div style={{ maxWidth: 600, margin: '80px auto', textAlign: 'center', padding: '0 24px' }}>
          <h2>Article not found</h2>
          <p>The article "{slug}" doesn't exist yet.</p>
          <button className="btn-primary" onClick={() => navigate('/help')}>Back to Help Center</button>
        </div>
      </div>
    );
  }

  const metaDescription = articleDescription(article);
  const canonicalUrl = articleUrl(article.slug);
  const pageTitle = articleTitle(article);
  const topic = topicByTitle(article.topic);
  const topicHref = topic ? topicPath(topic.slug) : '/help';

  return (
    <div className="article-page">
      {/* Per-article metadata. Without this every help article shared the generic
          homepage title/description and had no canonical — 19 near-identical pages,
          which is exactly what stalls indexing. */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>

      <header className="art-header">
        <div className="help-crumbs">
          <Link to="/help">Help Center</Link>
          <span className="sep">/</span>
          <Link to={topicHref}>{article.topic}</Link>
          <span className="sep">/</span>
          <span>{article.title}</span>
        </div>
        <div className="art-header-inner">
          <span className="eyebrow">{article.topic}</span>
          <h1>{article.title}</h1>
          <div className="art-meta">
            <span><Clock size={14} /> {article.meta.read}</span>
            <span><RefreshCw size={14} /> {article.meta.updated}</span>
            <span><Monitor size={14} /> {article.meta.platform}</span>
          </div>
        </div>
      </header>

      <div className="art-body">
        <div className="art-main">
          <p>{parseMarkdown(article.lead)}</p>
          {article.blocks.map((block, idx) => renderBlock(block, idx, { topic: article.topic, title: article.title }))}
        </div>

        <aside className="art-aside">
          {toc.length > 0 && (
            <div>
              <p className="art-toc-title">On this page</p>
              <ul className="art-toc">
                {toc.map(s => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} onClick={scrollTo(s.id)} className={active === s.id ? 'active' : ''}>{s.t}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="art-aside-cta">
            <h4>New to Chaptery?</h4>
            <p>The full walkthrough builds a world from scratch, start to finish.</p>
            <Link to="/help/tutorial">Start the tutorial <ArrowRight size={14} /></Link>
          </div>
        </aside>
      </div>

      <div className="art-feedback">
        <div className="art-feedback-inner">
          {fb === null ? (
            <>
              <strong>Was this article helpful?</strong>
              <div className="art-feedback-btns">
                <button className="art-fb-btn" onClick={() => setFb('yes')}><ThumbsUp size={15} /> Yes</button>
                <button className="art-fb-btn" onClick={() => setFb('no')}><ThumbsDown size={15} /> No</button>
              </div>
            </>
          ) : (
            <span className="art-fb-thanks"><CheckCircle size={17} /> Thanks for your feedback!</span>
          )}
        </div>
      </div>

      <section className="art-related">
        <h2>Related articles</h2>
        <div className="art-related-grid">
          {article.related.map(r => (
            <Link className="art-related-card" key={r.slug} to={articlePath(r.slug)}>
              <span className="rc-tag">{r.tag}</span>
              <h4>{r.t}</h4>
              <span className="rc-link">Read article</span>
            </Link>
          ))}
        </div>
      </section>

    </div>
  );
};

export default HelpArticlePage;
